import { Box } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  CATEGORY_PRODUCTS_PAGE_SIZE,
  useCategory,
} from "../../hooks/queries/useCategory";
import { useValueChain } from "../../hooks/queries/useValueChain";
import type { CategoryDetailParams } from "../../services/productPlaneApi";
import type { EntityDetailProps } from "../../types/entityRegistry";
import type { MainEntity } from "../../types/entityTypes";
import {
  CategoryOverview,
  CategoryProducts,
  hasCategoryOverview,
} from "../CategoryDetail/CategoryBlocks";
import { UNLABELLED_CATEGORY } from "../CategoryDetail/CategoryHead";
import DetailSections, { DetailSection } from "../Detail/DetailSections";
import { EntityNavigationProvider } from "../Detail/EntityLink";
import SourceTable from "../Detail/SourceTable";
import { SourceJumpProvider } from "../Detail/sourceJump";
import EntityFunctionalHeader from "../Layout/EntityDetailOverlay/EntityFunctionalHeader";
import { EntityDetailStatus } from "../Layout/EntityDetailStatus";
import { ChainCardRef } from "../ValueChain/valueChainState";
import KindHead from "./KindHead";
import { makeRootAction } from "./MakeRootButton";
import { chainSections, facilitiesSection } from "./chainSections";
import { neighbourParams, useMakeRoot, usePageChain } from "./kindChain";

// Width (px) the view keeps to on a page of its own
const STANDALONE_WIDTH = 1200;

// A count for the strip, where the section holds anything to count
const countOf = (count: number): number | undefined =>
  count > 0 ? count : undefined;

/**
 * The detail of a kind: the registry's Detail component of the type.
 *
 * A kind is a curated class of type rank and is named by its category id.
 * The view reads the category for its head, products and sources, and the
 * value chain one step to each side for its makers and its neighbours.
 */
const KindDetailView: React.FC<EntityDetailProps> = ({
  entityId,
  mode = "overlay",
  onEntityClick,
  onClose,
  scrollContainerRef,
}) => {
  // The page of products asked for; another kind starts at the first
  const [asked, setAsked] = useState<{
    entityId: string;
    paging: CategoryDetailParams;
  }>({ entityId, paging: {} });
  const paging = asked.entityId === entityId ? asked.paging : {};
  const { category, loading, error } = useCategory(entityId || null, paging);
  const { data: chain } = useValueChain(
    entityId ? neighbourParams("kind", entityId) : null,
  );

  const page = usePageChain();
  const makeRoot = useMakeRoot();

  const ownScrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollRef = scrollContainerRef ?? ownScrollContainerRef;

  // The kind as a root: by its id on the curated list once the read names it
  const root = useMemo<ChainCardRef>(
    () => ({ type: "kind", id: chain?.root.id ?? entityId }),
    [chain, entityId],
  );

  const sections = useMemo<DetailSection[]>(
    () =>
      category
        ? [
            {
              id: "overview",
              label: "Overview",
              content: hasCategoryOverview(category) && (
                <CategoryOverview category={category} />
              ),
            },
            // The page's chain names the card by the id the kind was opened
            // by: its makers, the kinds it is built into and its parts
            ...chainSections("kind", chain, page?.chain, entityId, makeRoot),
            {
              id: "products",
              label: "Products",
              count: countOf(category.products.total),
              content: category.products.items.length > 0 && (
                <CategoryProducts
                  page={category.products}
                  onOpen={(product) => onEntityClick?.(product.id, product.type)}
                  onPage={(offset) =>
                    setAsked({
                      entityId,
                      paging: {
                        products_limit: CATEGORY_PRODUCTS_PAGE_SIZE,
                        products_offset: offset,
                      },
                    })
                  }
                />
              ),
            },
            // The production sites of the makers in the kind's own read
            facilitiesSection(chain),
            {
              id: "sources",
              label: "Sources",
              count: countOf(category.source_count),
              content: category.sources.items.length > 0 && (
                <SourceTable
                  sources={category.sources.items}
                  total={category.sources.total}
                  mode={mode}
                />
              ),
            },
          ]
        : [],
    [category, chain, page, makeRoot, onEntityClick, entityId, mode],
  );

  // What the header reads: it shows the type and opens the full page
  const mainEntity = useMemo<MainEntity | null>(
    () =>
      category
        ? {
            id: category.id,
            name: category.label ?? UNLABELLED_CATEGORY,
            entityType: "kind",
          }
        : null,
    [category],
  );

  // Another kind starts at its top
  const categoryId = category?.id;
  useEffect(() => {
    if (categoryId && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [categoryId, scrollRef]);

  if (loading || error) {
    return <EntityDetailStatus loading={loading} error={error} mode={mode} />;
  }

  if (!category || !mainEntity) {
    return null;
  }

  return (
    <EntityNavigationProvider onEntityClick={onEntityClick}>
      <SourceJumpProvider>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <EntityFunctionalHeader
            mainEntity={mainEntity}
            onClose={onClose || (() => {})}
            mode={mode}
            extraActions={makeRootAction("kind", root)}
          />
          <Box ref={scrollRef} sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <Box
              sx={{
                maxWidth: mode === "standalone" ? STANDALONE_WIDTH : "100%",
                mx: "auto",
              }}
            >
              <KindHead
                category={category}
                categoryId={entityId}
                chain={chain ?? null}
                root={root}
              />
              <DetailSections
                sections={sections}
                scrollContainerRef={scrollRef}
              />
            </Box>
          </Box>
        </Box>
      </SourceJumpProvider>
    </EntityNavigationProvider>
  );
};

export default KindDetailView;
